import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  Stack,
  Text,
} from '@chakra-ui/react';
import { ManualOverride } from '@diamond/shared/types';
import { TextField } from '@diamond/shared/ui';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import { useEffect, useState } from 'react';
import { get, useFormContext, useWatch } from 'react-hook-form';

interface ManualFallbackFieldsProps {
  fieldIndex: number;
  showWarning?: boolean;
  warningMessage?: string;
  bankFieldLabel?: string;
  layout?: 'stack' | 'row';
}

const formatManualAmount = (value: string): string => {
  const normalized = value.replace(/\D/g, '');
  if (normalized === '') return '';

  return `Rp${Number(normalized).toLocaleString('id-ID')}`;
};

export const ManualFallbackFields = ({
  fieldIndex,
  showWarning = true,
  warningMessage = 'Gambar tidak terbaca. Unggah ulang dengan kualitas yang lebih baik.',
  bankFieldLabel = 'Nama Bank Pemilik',
  layout = 'stack',
}: ManualFallbackFieldsProps) => {
  const {
    control,
    getValues,
    register,
    setValue,
    formState: { errors },
  } = useFormContext<{ manual_overrides: ManualOverride[] }>();

  const bankFieldName = `manual_overrides.${fieldIndex}.sender_bank` as const;
  const nameFieldName =
    `manual_overrides.${fieldIndex}.sender_account_name` as const;
  const amountFieldName = `manual_overrides.${fieldIndex}.amount` as const;
  const [displayAmount, setDisplayAmount] = useState(() => {
    const currentAmount = getValues(amountFieldName);
    if (typeof currentAmount !== 'number') return '';

    return formatManualAmount(String(currentAmount));
  });
  const amountValue = useWatch({
    control,
    name: amountFieldName,
  });

  useEffect(() => {
    if (typeof amountValue !== 'number') {
      setDisplayAmount('');
      return;
    }

    setDisplayAmount(formatManualAmount(String(amountValue)));
  }, [amountValue]);

  const isRow = layout === 'row';
  const stackedFieldMaxW = '166px';
  const rowPairFieldSx = {
    flex: '1 1 152px',
    maxW: '100%',
    minW: 0,
  } as const;
  const fieldLabelProps = {
    color: '#373f41',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '17px',
    mb: '8px',
    whiteSpace: 'nowrap',
  } as const;

  const warningBanner = showWarning ? (
    <Flex
      align="flex-start"
      bg="#FEF2F2"
      borderRadius="4px"
      flex={isRow ? '1 1 100%' : undefined}
      gap="8px"
      minH="52px"
      px="8px"
      py="8px"
      w={isRow ? '100%' : undefined}
    >
      <Icon
        as={ErrorOutline}
        boxSize="20px"
        color="#FF0000"
        flexShrink={0}
        mt="8px"
      />
      <Text color="#FF0000" fontSize="10px" fontWeight={500} lineHeight="12px">
        {warningMessage}
      </Text>
    </Flex>
  ) : null;

  const amountField = (
    <FormControl isInvalid={!!get(errors, amountFieldName)}>
      <FormLabel aria-label="Nominal" {...fieldLabelProps}>
        Nominal
      </FormLabel>
      <Input
        aria-label="Nominal"
        bg="white"
        border="1px solid"
        borderColor="gray.33"
        borderRadius="8px"
        fontSize="16px"
        h="40px"
        inputMode="numeric"
        lineHeight="24px"
        name={amountFieldName}
        placeholder="Tulis disini"
        value={displayAmount}
        w="100%"
        onChange={(event) => {
          const normalized = event.target.value.replace(/\D/g, '');
          setDisplayAmount(formatManualAmount(normalized));
          setValue(
            amountFieldName,
            normalized === '' ? undefined : Number(normalized),
            {
              shouldDirty: true,
              shouldTouch: true,
              shouldValidate: true,
            }
          );
        }}
      />
      <FormErrorMessage textColor="red">
        {get(errors, amountFieldName)?.message as string}
      </FormErrorMessage>
    </FormControl>
  );

  const bankField = (
    <TextField
      bg="white"
      border="1px solid"
      borderColor="gray.33"
      borderRadius="8px"
      errors={errors}
      fontSize="16px"
      h="40px"
      label={bankFieldLabel}
      labelProps={fieldLabelProps}
      lineHeight="24px"
      maxLength={100}
      name={bankFieldName}
      placeholder="Tulis disini"
      register={register}
      w="100%"
    />
  );

  const accountNameField = (
    <TextField
      bg="white"
      border="1px solid"
      borderColor="gray.33"
      borderRadius="8px"
      errors={errors}
      fontSize="16px"
      h="40px"
      label="Nama Pemilik Rekening"
      labelProps={fieldLabelProps}
      lineHeight="24px"
      maxLength={100}
      name={nameFieldName}
      placeholder="Tulis disini"
      register={register}
      w="100%"
    />
  );

  if (isRow) {
    return (
      <Flex align="flex-start" flexWrap="wrap" gap="16px" w="100%">
        {warningBanner}
        <Box {...rowPairFieldSx}>{amountField}</Box>
        <Box {...rowPairFieldSx}>{bankField}</Box>
        <Box
          data-testid="ocr-account-name-field"
          flex="1 1 100%"
          maxW="100%"
          minW={0}
          w="100%"
        >
          {accountNameField}
        </Box>
      </Flex>
    );
  }

  return (
    <Stack spacing={3}>
      {warningBanner}
      <Box maxW={stackedFieldMaxW}>{amountField}</Box>
      <Box maxW={stackedFieldMaxW}>{bankField}</Box>
      <Box maxW={stackedFieldMaxW}>{accountNameField}</Box>
    </Stack>
  );
};
